// The Vue build version to load with the `import` command
// (runtime-only or standalone) has been set in webpack.base.conf with an alias.

/* eslint-disable import/extensions */
/* eslint-disable vue/component-definition-name-casing */

// Package imports for main libraries.
import 'core-js/stable';
import * as Sentry from '@sentry/vue';
import * as Vue from 'vue';

// Package imports for vue component plugin libraries.
import VueClipboard from 'vue3-clipboard';

// Package imports from local libraries.
import * as router from 'router';
import App from 'src/App.vue'; // eslint-disable-line import/first
import { CI_TESTING, STANDALONE } from 'assets/js/api/environment';
import store from 'store'; // eslint-disable-line import/first
import './polyfills/element-remove';
import 'dialog-polyfill';
import VueBarcodeScanner from 'assets/js/vue-barcode-scanner';
import VueFormGenerator from 'components/Widgets/FormGenerator';
import VueSweetAlert from 'assets/js/vue-swal';

// Local libraries.
import {
  MixinComponents,
  MixinComputedDebug,
  MixinDirectives,
  MixinFilters,
  MixinMethods,
  MixinProps,
  MixinWatchDebug,
} from 'assets/js/mixins';
import { SpawnWSHandler } from 'assets/js/api/websockets';

// Custom fields for vue-form-generator
import fieldQLabel from 'components/Reservation/Form/Fields/FieldQLabel.vue';
import fieldQCheckboxes
  from 'components/Reservation/Form/Fields/FieldQCheckboxes.vue';
import fieldQDate from 'components/Reservation/Form/Fields/FieldQDate.vue';
import fieldQInvoiceItem
  from 'components/Reservation/Form/Fields/FieldQInvoiceItem.vue';
import fieldQSelect from 'components/Reservation/Form/Fields/FieldQSelect.vue';
import fieldQSwitch from 'components/Reservation/Form/Fields/FieldQSwitch.vue';
import fieldQTable
  from 'components/Reservation/Form/Fields/FieldQTable.vue';
import fieldQTextArea
  from 'components/Reservation/Form/Fields/FieldQTextArea.vue';
import fieldQTextInput
  from 'components/Reservation/Form/Fields/FieldQTextInput.vue';
import fieldQFileUpload
  from 'components/Reservation/Form/Fields/FieldQFileUpload.vue';

declare global {
  interface Window {
    Cypress: any;
    app: any;
  }
}

const localEnv = CI_TESTING ? 'testing' : process.env.NODE_ENV;

// mainChContainer is the container for global channel which is spawned by the
// websocket connection.
const mainChContainer = { mainCh: null, nonce: 0, websocket: null };
// @ts-ignore
window.fetchMainChContainer = () => mainChContainer;

// channelSetter lets us know when the main channel has been set, so things
// can wait on the promise until then.
let channelSetter = null;
const channelSet = new Promise((resolve) => {
  channelSetter = resolve;
});

// assignMainCh is a helper function that lets us spawn and assign the main
// channel. If the channel has already been spawned, it just returns the active
// channel.
const assignMainCh = (jwt, process, toaster) => {
  if (!mainChContainer.mainCh || mainChContainer.mainCh.isClosed()) {
    const mainChLocal = SpawnWSHandler(process, toaster, store);

    // Bypass for development mode tools, which inject debug tooling into
    // the window for websockets.
    if (localEnv === 'development') {
      mainChContainer.mainCh = mainChLocal.mainCh;
      mainChContainer.websocket = mainChLocal.websocket;
      // @ts-ignore
      window.WEBSOCKET_CHANNEL_DEBUG = mainChLocal;
    } else {
      mainChContainer.mainCh = mainChLocal.mainCh;
      mainChContainer.websocket = mainChLocal.websocket;
    }
    mainChContainer.nonce += 1;
  }

  return mainChContainer.mainCh;
};

let mixinComputedDebug = {};
let mixinWatchDebug = {};
if (localEnv === 'development') {
  mixinComputedDebug = MixinComputedDebug;
  mixinWatchDebug = MixinWatchDebug;
}

/*
 * DEPRECATED DECEMBER 21, 2021.
if (STANDALONE) {
  store.dispatch('GetLicense', { force: false });
}
*/

/* eslint-disable no-new */
const app = Vue.createApp(App)
  .use(store)
  .use(router.Router)
  .use(VueBarcodeScanner)
  .use(VueSweetAlert)
  .use(VueFormGenerator)
  .use(VueClipboard, {
    autoSetContainer: true,
    appendToBody: true,
  })
  .mixin({
    computed: { ...mixinComputedDebug },
    components: { ...MixinComponents },
    directives: { ...MixinDirectives },
    filters: { ...MixinFilters },

    methods: {
      ...MixinMethods({
        assignMainCh,
        channelSet,
        channelSetter,
        mainChContainer,
      }),
    },

    props: { ...MixinProps },
    watch: { ...mixinWatchDebug },
  })
  // vue-form-generator requires its field components to be registered according
  // to the regex /(field)[A-Z][a-z]*/.
  .component('fieldDate', fieldQDate)
  .component('fieldQCheckboxes', fieldQCheckboxes)
  .component('fieldQFileUpload', fieldQFileUpload)
  .component('fieldQInvoiceItem', fieldQInvoiceItem)
  .component('fieldQLabel', fieldQLabel)
  .component('fieldQSelect', fieldQSelect)
  .component('fieldQSwitch', fieldQSwitch)
  .component('fieldQTable', fieldQTable)
  .component('fieldQTextArea', fieldQTextArea)
  .component('fieldQTextInput', fieldQTextInput);

// Setup sentry error tracking. Skip tracking if we're on dev.
if (localEnv !== 'development') {
  app.config.devtools = false;
  app.config.debug = false;
  app.config.silent = true;

  if (!STANDALONE && localEnv !== 'testing') {
    Sentry.init({
      app,
      dsn: "https://de0e658612f1f511e891cf9a6c72bde2@s.qreserve.com/3",

      replaysSessionSampleRate: 0.0,
      replaysOnErrorSampleRate: 1.0,

      release: String(process.env.VERSION).replace(/"/g, ''),

      tracesSampleRate: 0.1,
      integrations: [
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
        }),

        Sentry.browserTracingIntegration({ router: router.Router }),
        Sentry.rewriteFramesIntegration(),
      ],

      environment: process.env.NODE_ENV,
      ignoreErrors: [
        // ******************
        // * General Errors *
        // ******************
        // It's common for a token to expire and subsequent requests and actions
        // to fail with this error.
        /Token is expired/,
        /AUTHORIZATION_ERROR/,
        /Error communicating with QReserve/,

        // *************************
        // * Full Calendar Related *
        // *************************
        // This is a FC bug but it should only happen when events are draggable
        // so odd. Seems to only hit on mobile. For more information, see this
        // bug report: https://github.com/fullcalendar/fullcalendar/issues/5001
        /isWithinClipping/,

        // This can show up only in Safari on iOS sometimes.type
        /this.rowNodes\[a\].resource/,

        // ******************
        // * Browser Quirks *
        // ******************
        // This is an error from chrome that can be safely ignored.
        // https://stackoverflow.com/questions/49384120
        /^ResizeObserver loop limit exceeded$/,

        // Safari will throw this after a while and we cannot fix it.
        /^No more than 1024 pending puts are allowed on a single channel.$/,

        // Very noisy websocket errors.
        /main channel is closed; the websocket is probably not connected/,

        // GoogleMaps internal error that hits old Safari on iOS sometimes.
        /^Could not load "util".$/,

        // From Office.js
        /replaceState is not a function/,

        // Vue Router Insanity.
        /^Avoided redundant navigation to current location$/,
        /^(Redirected when going from).*(via a navigation guard.)$/,
      ],
    });
  }
}

app.mount('#qreserve-app');

if (window.Cypress) {
  // only available during E2E tests
  window.app = app;
}
